import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';

export type FrameScheduler = (callback: () => void) => () => void;

export const afterNextPaint: FrameScheduler = (callback) => {
  if (typeof requestAnimationFrame !== 'function') {
    const timer = setTimeout(callback, 0);
    return () => clearTimeout(timer);
  }

  let secondFrame: number | undefined;
  const firstFrame = requestAnimationFrame(() => {
    secondFrame = requestAnimationFrame(callback);
  });

  return () => {
    cancelAnimationFrame(firstFrame);
    if (secondFrame !== undefined) cancelAnimationFrame(secondFrame);
  };
};

export function usePaintSettled(
  active: boolean,
  scheduleFrame: FrameScheduler = afterNextPaint,
  scopeKey = 'default'
): boolean {
  const [settledScope, setSettledScope] = useState<string>();

  useEffect(() => {
    if (!active || settledScope === scopeKey) return undefined;

    let cancelled = false;
    const cancel = scheduleFrame(() => {
      if (!cancelled) setSettledScope(scopeKey);
    });

    return () => {
      cancelled = true;
      cancel();
    };
  }, [settledScope, active, scheduleFrame, scopeKey]);

  return active && settledScope === scopeKey;
}

type GridRowsReadinessSignal = { loaded: boolean; scopeKey: string };

const GridRowsReadinessContext = createContext<GridRowsReadinessSignal | null>(null);

type GridRowsReadinessProviderProps = { children: ReactNode; loaded?: boolean; loading?: boolean; scopeKey?: string };

export function GridRowsReadinessProvider({
  children,
  loaded,
  loading,
  scopeKey = 'default',
}: GridRowsReadinessProviderProps) {
  return (
    <GridRowsReadinessContext.Provider value={{ loaded: loaded ?? loading !== true, scopeKey }}>
      {children}
    </GridRowsReadinessContext.Provider>
  );
}

export function useGridRowsPainted(dependentScopeKey = 'default'): boolean {
  const signal = useContext(GridRowsReadinessContext);
  const combinedScopeKey = signal ? `${signal.scopeKey}\u0000${dependentScopeKey}` : 'outside-grid';
  const painted = usePaintSettled(signal?.loaded === true, afterNextPaint, combinedScopeKey);

  return signal ? painted : true;
}
